/**
 * 支払方法・下取り・備考のパネル。
 */
import type { QuoteInstallments, QuotePaymentMethod, QuoteTradeInType } from '@goenhub/shared';
import type { ReactNode } from 'react';

import { CommitInput, CommitTextarea } from './CommitInput';
import { formatYen, type EditorState } from './model';

const INSTALLMENTS: QuoteInstallments[] = [12, 24, 36, 48, 60];

type TermsState = Pick<
  EditorState,
  'paymentMethod' | 'installments' | 'hasTradeIn' | 'tradeInType' | 'tradeInAmount' | 'remarks'
>;

interface Props {
  value: TermsState;
  onChange: (patch: Partial<TermsState>) => void;
  onCommitTradeInAmount: (text: string) => void;
  onCommitRemarks: (text: string) => void;
}

const fieldClass =
  'w-full px-2.5 rounded-md border border-[var(--border)] bg-[var(--bg-input)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent)] placeholder-shown:border-dashed placeholder-shown:border-[var(--accent-amber)] placeholder-shown:bg-[var(--accent-amber-subtle)]';

export function TermsPanel({ value, onChange, onCommitTradeInAmount, onCommitRemarks }: Props) {
  return (
    <>
      <Field label="支払方法">
        <Segments<QuotePaymentMethod>
          value={value.paymentMethod}
          options={[
            ['transfer', '銀行振込'],
            ['credit_card', 'クレジットカード'],
          ]}
          onChange={(v) => onChange({ paymentMethod: v })}
        />
      </Field>

      {value.paymentMethod === 'credit_card' && (
        <Field label="分割回数">
          <Segments<QuoteInstallments>
            value={value.installments}
            options={INSTALLMENTS.map((n) => [n, `${n} 回`])}
            onChange={(v) => onChange({ installments: v })}
          />
        </Field>
      )}

      <Field label="下取り">
        <Segments<'0' | '1'>
          value={value.hasTradeIn ? '1' : '0'}
          options={[
            ['0', 'なし'],
            ['1', 'あり'],
          ]}
          onChange={(v) => onChange({ hasTradeIn: v === '1' })}
        />
      </Field>

      {value.hasTradeIn && (
        <div className="space-y-3.5 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
          <Field label="下取りの種類">
            <Segments<QuoteTradeInType>
              value={value.tradeInType}
              options={[
                ['discount', '値引き'],
                ['cashback', 'キャッシュバック'],
              ]}
              onChange={(v) => onChange({ tradeInType: v })}
            />
            <p className="text-[11px] text-[var(--text-muted)]">
              値引きは合計金額から差し引きます。キャッシュバックは合計金額を変えません。
            </p>
          </Field>
          <Field label="下取り金額 (税込)" htmlFor="quote-tradein-amount">
            <CommitInput
              id="quote-tradein-amount"
              value={value.tradeInAmount != null ? formatYen(value.tradeInAmount) : ''}
              placeholder="例: 50000"
              inputMode="numeric"
              onCommit={onCommitTradeInAmount}
              className={`${fieldClass} h-9 text-right tabular-nums`}
            />
          </Field>
        </div>
      )}

      <Field label="備考" htmlFor="quote-remarks">
        <CommitTextarea
          id="quote-remarks"
          value={value.remarks}
          rows={5}
          placeholder="顧客向けの追記事項（任意）"
          onCommit={onCommitRemarks}
          className={`${fieldClass} py-2 min-h-24 leading-relaxed`}
        />
        <p className="text-[11px] text-[var(--text-muted)]">
          Enter で確定、Shift + Enter で改行します。
        </p>
      </Field>
    </>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="block text-xs font-medium text-[var(--text-secondary)]">
          {label}
        </label>
      ) : (
        <div className="text-xs font-medium text-[var(--text-secondary)]">{label}</div>
      )}
      {children}
    </div>
  );
}

function Segments<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap rounded-md border border-[var(--border)] overflow-hidden">
      {options.map(([v, label], i) => (
        <button
          key={String(v)}
          type="button"
          aria-pressed={v === value}
          onClick={() => v !== value && onChange(v)}
          className={`flex-1 px-3 py-1.5 text-[13px] whitespace-nowrap transition-colors ${
            i > 0 ? 'border-l border-[var(--border)]' : ''
          } ${
            v === value
              ? 'bg-[var(--accent)] text-white'
              : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
